import {makeAutoObservable} from 'mobx';
import {toast} from 'react-toastify';
import CommentsRepository from '../../../../../data/repositories/CommentsRepository';
import GradebookRepository from '../../../../../data/repositories/GradebookRepository';
import {
  CommentModel,
  CommentsListRequest,
  CourseComment,
} from '../../../../../data/models/Comments/CommentModel';
import {PaginatedApiReponse} from '../../../../../data/models/ApiResponse';
import {GradebookLesson} from '../../../../../data/models/Course/Gradebook';
import {getErrorFromCode} from '../../../../../data/errorCodes';
import {toastConfig} from '../../../../../uiToolkit/Toast/toastConfig';
import LoadingModule from '../../../../../modules/LoadingModule/LoadingModule';

const COMMENTS_PAGE_SIZE = 100;

class CourseAnalyticsCommentsStore {
  private commentsRepo = new CommentsRepository();
  private gradebookRepo = new GradebookRepository();
  private loadingModule = new LoadingModule();

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public async getComments(courseId: string, lessons: GradebookLesson[]) {
    this.loadingModule.startLoading();
    const results = await Promise.all([
      this.getAllEntityComments(courseId),
      ...lessons.map(lesson => this.getAllEntityComments(lesson.lessonId)),
    ]);
    this.loadingModule.endLoading();

    const failed = results.find(result => result.error);
    if (failed) {
      toast.error(getErrorFromCode(failed.error!), toastConfig);
      return [];
    }

    const seen = new Set<string>();
    const comments: CourseComment[] = [];
    results.forEach((result, index) => {
      const microreelId = index === 0 ? undefined : lessons[index - 1].lessonId;
      result.comments.forEach(comment => {
        if (seen.has(comment.id)) return;
        seen.add(comment.id);
        comments.push({...comment, microreelId});
      });
    });
    return comments;
  }

  // Entity list only returns root comments, replies are fetched per comment
  private async getAllEntityComments(
    entityId: string,
  ): Promise<{comments: CommentModel[]; error?: string}> {
    const roots = await this.getAllPages(page =>
      this.commentsRepo.getEntityComments(entityId, page),
    );
    if (roots.error) {
      return roots;
    }
    const replies = await Promise.all(
      roots.comments
        .filter(comment => comment.repliesCount > 0)
        .map(comment =>
          this.getAllPages(page =>
            this.commentsRepo.getCommentReplies(comment.id, page),
          ),
        ),
    );
    const failed = replies.find(result => result.error);
    if (failed) {
      return failed;
    }
    return {
      comments: [...roots.comments, ...replies.flatMap(x => x.comments)],
    };
  }

  private async getAllPages(
    fetchPage: (
      request: CommentsListRequest,
    ) => Promise<PaginatedApiReponse<CommentModel>>,
  ): Promise<{comments: CommentModel[]; error?: string}> {
    const comments: CommentModel[] = [];
    let page = 0;
    let pagesCount = 1;
    while (page < pagesCount) {
      const result = await fetchPage({
        page,
        pageSize: COMMENTS_PAGE_SIZE,
        orderAsk: false,
      });
      if (!result.success || !result.data) {
        return {comments, error: result.errors?.[0] || 'unknown'};
      }
      comments.push(...result.data.data);
      pagesCount = result.data.pagesCount;
      page++;
    }
    return {comments};
  }

  public async getCourseMicroreels(organizationId: string, courseId: string) {
    const result = await this.gradebookRepo.getCourseGradebook(
      organizationId,
      courseId,
    );
    if (result.success && result.data) {
      return [...result.data.lessons].sort(
        (x, y) => x.folderOrder - y.folderOrder || x.order - y.order,
      );
    }
    return [];
  }
}

export default CourseAnalyticsCommentsStore;
